// eBay Partner Network (EPN) affiliate tagging for every outbound eBay link and
// every eBay API call made by the price board.
//
// Two independent things have to carry the campaign id for Pal Stars to be
// credited for a sale:
//   1. Outbound links a visitor clicks (/prices, /cards/$code) — EPN rover
//      parameters on the eBay search URL.
//   2. API reads (Browse / Marketplace Insights) — the
//      `X-EBAY-C-ENDUSERCTX: affiliateCampaignId=...,affiliateReferenceId=...`
//      header, which makes eBay return affiliate-tagged item URLs.
//
// The campaign id is public information (it is visible in every click URL), so
// it lives in code with an env override for rotation.

/** 10-digit EPN campaign id for palstars.com. */
export const EBAY_CAMPAIGN_ID = "5339199836";

/** Free-form sub-id EPN reports commissions under. */
export const EBAY_REFERENCE_ID = "Palstars";

/** Header value eBay expects on affiliate-attributed API calls. */
export const EBAY_ENDUSERCTX = `affiliateCampaignId=${EBAY_CAMPAIGN_ID},affiliateReferenceId=${EBAY_REFERENCE_ID}`;

/** EPN rotation ids, per eBay site. */
const ROTATION: Record<string, string> = {
  "ebay.com": "711-53200-19255-0",
  "ebay.co.uk": "710-53481-19255-0",
  "ebay.de": "707-53477-19255-0",
};

/**
 * EPN sub-id (`customid`). EPN reports commissions per sub-id, which is how a
 * confirmed sale is attributed back to the exact card or sealed product whose
 * link was clicked — the missing half of clicks-to-sales tracking. Kept to the
 * safe character set and length EPN accepts.
 */
export function ebaySubId(targetKey?: string): string {
  if (!targetKey) return EBAY_REFERENCE_ID;
  const slug = String(targetKey)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return slug ? `${EBAY_REFERENCE_ID}-${slug}` : EBAY_REFERENCE_ID;
}

/**
 * Adds EPN tracking parameters to an eBay URL. Non-eBay URLs and URLs that are
 * already tagged are returned untouched, so this is safe to apply broadly.
 * `targetKey` (card code or sealed product key) becomes the EPN sub-id.
 */
export function ebayAffiliateUrl(raw: string, targetKey?: string): string {
  try {
    const url = new URL(raw);
    const host = url.hostname.toLowerCase().replace(/^www\./, "");
    const mkrid = ROTATION[host];
    if (!mkrid) return raw;
    if (url.searchParams.has("campid")) return raw;
    url.searchParams.set("mkcid", "1");
    url.searchParams.set("mkrid", mkrid);
    url.searchParams.set("siteid", host === "ebay.co.uk" ? "3" : host === "ebay.de" ? "77" : "0");
    url.searchParams.set("campid", EBAY_CAMPAIGN_ID);
    url.searchParams.set("customid", ebaySubId(targetKey));
    url.searchParams.set("toolid", "10001");
    url.searchParams.set("mkevt", "1");
    return url.toString();
  } catch {
    return raw;
  }
}
